(() => {
  if (window.__daktenterRentalJourneyInitialized) return;
  window.__daktenterRentalJourneyInitialized = true;

  const roots = [...document.querySelectorAll('[data-rental-journey]')];
  if (!roots.length) return;

  const clean = (value) => String(value || '').trim();
  const pageType = roots[0].dataset.pageType || 'national_rental';

  const publish = (name, parameters = {}) => {
    const payload = Object.fromEntries(
      Object.entries({ page_type: pageType, ...parameters }).filter(([, value]) => value !== '' && value != null),
    );
    try {
      if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, payload);
    } catch (error) {
      console.warn('Rental Customer Event publish failed', error);
    }
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...payload });
    document.dispatchEvent(new CustomEvent('daktenter:rental-event', { detail: { name, parameters: payload } }));
  };

  const eventPayload = (element) => ({
    source_section: clean(element.dataset.sourceSection),
    model: clean(element.dataset.model),
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-rental-event]');
    if (!target) return;
    publish(target.dataset.rentalEvent, eventPayload(target));
  });

  const cards = [...document.querySelectorAll('[data-rental-model-card]')];
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.target.dataset.viewTracked === 'true') return;
        entry.target.dataset.viewTracked = 'true';
        publish('rental_model_card_view', { source_section: 'model_cards', model: clean(entry.target.dataset.model) });
        observer.unobserve(entry.target);
      });
    }, { threshold: .55 });
    cards.forEach((card) => observer.observe(card));
  }

  const amenityList = document.querySelector('.rental-journey__included-grid');
  const amenityItems = amenityList ? [...amenityList.querySelectorAll('[data-rental-amenity]')] : [];
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#rental-fit-check"]');
    if (!link || !roots.some((root) => root.contains(link))) return;
    const target = document.getElementById('rental-fit-check');
    if (!target) return;
    event.preventDefault();
    if (window.location.hash !== '#rental-fit-check') window.history.pushState(null, '', '#rental-fit-check');
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    let interrupted = false;
    const cancelCorrection = () => { interrupted = true; };
    window.addEventListener('wheel', cancelCorrection, { passive: true, once: true });
    window.addEventListener('touchstart', cancelCorrection, { passive: true, once: true });
    window.setTimeout(() => {
      window.removeEventListener('wheel', cancelCorrection);
      window.removeEventListener('touchstart', cancelCorrection);
      if (interrupted) return;
      const expectedTop = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
      if (Math.abs(target.getBoundingClientRect().top - expectedTop) > 32) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }, 1200);
  });
  // One opacity owner per below-fold section. Prepare nearby media before the
  // section reaches its later reveal threshold; never wait on third-party apps.
  const mobileRevealSuffixes = [
    '__media_with_content_cJ6WFD', '__1758371116644dc1fc',
    '__rental_journey_after_prices', '__roofbar_fitment_selector_AGDbEA',
    '__section_aefgBL',
  ];
  const revealTargets = mobileRevealSuffixes
    .map(suffix => document.querySelector(`#MainContent > .shopify-section[id$="${suffix}"]`))
    .filter(element => element && getComputedStyle(element).display !== 'none');
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const prepared = new WeakMap();
    const prepare = element => {
      if (prepared.has(element)) return prepared.get(element);
      const images = [...element.querySelectorAll('img[loading="lazy"]')];
      const decoding = images.map(image => {
        image.loading = 'eager';
        return image.decode?.().catch(() => {}) || Promise.resolve();
      });
      element.querySelectorAll('video[preload="none"]').forEach(video => { video.preload = 'metadata'; });
      element.querySelectorAll('iframe[loading="lazy"]').forEach(frame => { frame.loading = 'eager'; });
      const ready = Promise.race([
        Promise.allSettled([...decoding, document.fonts?.ready || Promise.resolve()]),
        new Promise(resolve => setTimeout(resolve, 500)),
      ]);
      prepared.set(element, ready);
      return ready;
    };
    const reveal = element => {
      if (element.dataset.revealRequested === 'true') return;
      element.dataset.revealRequested = 'true';
      prepare(element).then(() => {
        if (element.dataset.revealRequested !== 'true') return;
        element.classList.remove('is-pending');
        element.classList.add('is-revealed');
      });
    };
    const visibleEnough = element => {
      const box = element.getBoundingClientRect();
      const visible = Math.max(0, Math.min(box.bottom, innerHeight) - Math.max(box.top, 0));
      const needed = Math.min(box.height * .3, Math.max(88, innerHeight * .2));
      return visible >= needed;
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting && visibleEnough(target)) reveal(target);
        else if (!isIntersecting && !target.contains(document.activeElement)) {
          target.dataset.revealRequested = 'false';
          target.classList.remove('is-revealed');
          target.classList.add('is-pending');
        }
      });
    }, { threshold: [0, .01, .025, .05, .1, .2, .3] });
    const preloadObserver = new IntersectionObserver(entries => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        prepare(target).then(() => { if (visibleEnough(target)) reveal(target); });
        preloadObserver.unobserve(target);
      });
    }, { rootMargin: '150% 0px', threshold: 0 });
    const layoutObserver = 'ResizeObserver' in window
      ? new ResizeObserver(entries => entries.forEach(({ target }) => { if (visibleEnough(target)) reveal(target); }))
      : null;
    revealTargets.forEach(element => {
      element.classList.add('rental-reveal');
      if (visibleEnough(element)) reveal(element);
      else element.classList.add('is-pending');
      element.addEventListener('focusin', () => reveal(element));
      observer.observe(element);
      preloadObserver.observe(element);
      layoutObserver?.observe(element);
    });
    window.addEventListener('pageshow', () => revealTargets.filter(visibleEnough).forEach(reveal));
    window.addEventListener('hashchange', () => revealTargets.filter(visibleEnough).forEach(reveal));
  }
  if (amenityItems.length) {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      amenityItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      amenityList.classList.add('is-animation-ready');
      amenityItems.forEach((item, index) => {
        item.style.setProperty('--amenity-delay', `${Math.min(index, 6) * 42}ms`);
      });
      const amenityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          amenityObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: .18 });
      amenityItems.forEach((item) => amenityObserver.observe(item));
    }
  }

  const form = document.querySelector('#RentalJourneyForm');
  if (form) {
    const roofBarsChoice = form.querySelector('[data-roof-bars-choice]');
    const carDetails = form.querySelector('[data-rental-car-details]');
    const updateCarDetails = () => {
      if (!roofBarsChoice || !carDetails) return;
      const needsCheck = roofBarsChoice.selectedIndex > 1;
      carDetails.hidden = !needsCheck;
      carDetails.disabled = !needsCheck;
      roofBarsChoice.setAttribute('aria-expanded', String(needsCheck));
      carDetails.querySelectorAll('input').forEach(input => { input.required = needsCheck; });
    };
    roofBarsChoice?.addEventListener('change', updateCarDetails);
    updateCarDetails();
    const startDate = form.querySelector('[name="contact[Startdatum]"]');
    const endDate = form.querySelector('[name="contact[Einddatum]"]');
    startDate?.addEventListener('change', () => { endDate.min = startDate.value; });
    const referenceInput = form.querySelector('[data-rental-reference-input]');
    const success = document.querySelector('[data-rental-form-success]');
    const output = document.querySelector('[data-rental-reference-output]');
    let reference = sessionStorage.getItem('daktenterRentalReference') || '';
    const createReference = () => {
      const now = new Date();
      const date = `${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const suffix = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
      return `DR-${date}-${suffix}`;
    };
    if (!reference) reference = createReference();
    if (referenceInput) referenceInput.value = reference;
    if (success && output) {
      output.textContent = reference;
      success.focus();
      sessionStorage.removeItem('daktenterRentalReference');
    }
    let started = false;
    form.addEventListener('input', () => {
      if (started) return;
      started = true;
      publish('rental_form_start', { source_section: 'rental_request_form' });
    }, { once: true });
    form.addEventListener('submit', () => {
      sessionStorage.setItem('daktenterRentalReference', reference);
      const roofBars = clean(form.querySelector('[data-roof-bars-choice]')?.value);
      const fulfilment = clean(form.querySelector('[data-fulfilment-choice]')?.value);
      publish('rental_form_submit', {
        source_section: 'rental_request_form',
        roof_bars_needed: roofBars === 'Ik heb dakdragers nodig' ? 'yes' : roofBars ? 'unknown_or_no' : '',
        fulfilment_type: fulfilment ? fulfilment.toLowerCase().replace(/[^a-z0-9]+/g, '_') : '',
      });
    });
  }

  const fitCheck = document.querySelector('[data-roofbar-selector]');
  if (fitCheck) {
    let fitStarted = false;
    fitCheck.addEventListener('change', () => {
      if (fitStarted) return;
      fitStarted = true;
      publish('rental_fit_check_start', { source_section: 'fit_check' });
    });
    const result = fitCheck.querySelector('[data-result]');
    const noResult = fitCheck.querySelector('[data-no-result]');
    if ('MutationObserver' in window && (result || noResult)) {
      let completed = false;
      const observer = new MutationObserver(() => {
        if (completed) return;
        const resultVisible = result && !result.hidden && result.textContent.trim();
        const noResultVisible = noResult && !noResult.hidden;
        if (!resultVisible && !noResultVisible) return;
        completed = true;
        publish('rental_fit_check_complete', { source_section: 'fit_check', result_found: resultVisible ? 'yes' : 'no' });
      });
      if (result) observer.observe(result, { attributes: true, childList: true, subtree: true });
      if (noResult) observer.observe(noResult, { attributes: true });
    }
  }


})();
